import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface GroupRow {
  id: string;
  name: string;
  level: string | null;
  whatsapp_link: string | null;
  color: string | null;
  active: boolean;
}

export default function Whatsapp() {
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('zehrahoca_groups')
        .select('id, name, level, whatsapp_link, color, active')
        .order('created_at', { ascending: true });
      if (fetchError) throw fetchError;
      setGroups((data ?? []) as GroupRow[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleChange = () => fetchAll();
    window.addEventListener('zehrahoca:groups-changed', handleChange);
    return () => window.removeEventListener('zehrahoca:groups-changed', handleChange);
  }, [fetchAll]);

  async function copyLink(g: GroupRow) {
    if (!g.whatsapp_link) return;
    try {
      await navigator.clipboard.writeText(g.whatsapp_link);
      setCopiedId(g.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Panoya kopyalanamadı.');
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">WhatsApp grupları yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Gruplar yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={fetchAll} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">WhatsApp Grupları</h1>
          <p className="mt-1 text-sm text-foreground-600">
            Grup Yönetimi'nde tanımladığınız WhatsApp linkleri burada listelenir.
          </p>
        </div>
        <Link
          to="/panel/gruplar"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-team-line text-base" /> Grup Yönetimi'nde Düzenle
        </Link>
      </div>

      {groups.length === 0 ? (
        <div className="rounded-xl border border-background-200 bg-white p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
            <i className="ri-whatsapp-line text-2xl text-emerald-500" />
          </div>
          <p className="text-sm font-semibold text-foreground-700">Henüz grup bulunmuyor.</p>
          <p className="mt-1 text-xs text-foreground-500">Önce Grup Yönetimi'nden bir grup oluşturun.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <div
              key={g.id}
              className={`flex flex-col rounded-xl border bg-white p-5 transition hover:shadow-md ${
                g.active ? 'border-background-200' : 'border-background-200 opacity-70'
              }`}
            >
              <div className="mb-3 flex items-start justify-between">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-white"
                  style={{ backgroundColor: g.color ?? '#10b981' }}
                >
                  <i className="ri-whatsapp-line text-xl" />
                </div>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    g.active ? 'bg-emerald-50 text-emerald-600' : 'bg-background-100 text-foreground-500'
                  }`}
                >
                  <i className={g.active ? 'ri-checkbox-circle-fill' : 'ri-pause-circle-line'} />
                  {g.active ? 'Aktif' : 'Pasif'}
                </span>
              </div>

              <h3 className="font-semibold text-foreground-950 text-sm leading-tight">{g.name}</h3>
              {g.level && (
                <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-background-100 px-2 py-0.5 text-xs font-semibold text-foreground-700 w-fit">
                  <i className="ri-bar-chart-line" /> {g.level}
                </span>
              )}

              <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500">
                <i className={g.whatsapp_link ? 'ri-checkbox-circle-line text-emerald-500' : 'ri-error-warning-line text-foreground-400'} />
                {g.whatsapp_link ? 'Link tanımlı' : 'Link tanımlanmadı'}
              </p>

              <div className="mt-auto flex items-center gap-2 border-t border-background-100 pt-3 mt-4">
                <button
                  type="button"
                  onClick={() => copyLink(g)}
                  disabled={!g.whatsapp_link}
                  className={`inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition cursor-pointer ${
                    g.whatsapp_link ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-background-100 text-foreground-400 cursor-not-allowed'
                  }`}
                >
                  <i className={copiedId === g.id ? 'ri-check-line' : 'ri-link'} />
                  {copiedId === g.id ? 'Kopyalandı' : 'Linki Kopyala'}
                </button>
                {g.whatsapp_link && (
                  <a
                    href={g.whatsapp_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200"
                    title="Linki Aç"
                  >
                    <i className="ri-external-link-line text-base" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}